import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronDown, Mail, UserPlus, X, Send } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { db, functions } from '@/config/firebase';
import { collection, getDocs, doc, setDoc, deleteDoc, updateDoc, addDoc } from 'firebase/firestore';
import { employeeNamesService } from '@/services/employeeNames';
import { PersonSelect } from '../good-things/PersonSelect';
import { httpsCallable } from 'firebase/functions';

interface FeedbackNotificationRecipientsSectionProps {
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
}

interface Recipient {
  email: string;
  name?: string;
  enabled: boolean;
}

const FeedbackNotificationRecipientsSection: React.FC<FeedbackNotificationRecipientsSectionProps> = ({
  isExpanded,
  setIsExpanded
}) => {
  const { toast } = useToast();
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<string>('');
  const [availablePeople, setAvailablePeople] = useState<string[]>([]);

  // Load recipients and available people
  useEffect(() => {
    if (isExpanded) {
      loadRecipients();
      loadAvailablePeople();
    }
  }, [isExpanded]);

  const loadRecipients = async () => {
    try {
      setIsLoading(true);
      const recipientsSnapshot = await getDocs(collection(db, 'feedbackNotificationRecipients'));
      const recipientsList: Recipient[] = [];

      recipientsSnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        recipientsList.push({
          email: docSnap.id,
          name: data.name,
          enabled: data.enabled ?? true
        });
      });

      setRecipients(recipientsList.sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email)));
    } catch (error) {
      console.error('Error loading feedback recipients:', error);
      toast({
        title: 'Error Loading Recipients',
        description: 'Failed to load feedback notification recipients',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadAvailablePeople = async () => {
    try {
      const mappings = await employeeNamesService.getEmployeeNames();
      const peopleNames = mappings.map(m => m.displayName).filter(Boolean).sort();
      setAvailablePeople(peopleNames);
    } catch (error) {
      console.error('Error loading people:', error);
    }
  };

  const handleAddRecipient = async () => {
    if (!selectedPerson) {
      toast({
        title: 'No Person Selected',
        description: 'Please select a person to add as a feedback recipient',
        variant: 'destructive'
      });
      return;
    }

    try {
      // Get email for the selected person
      const email = await employeeNamesService.getEmailFromDisplayName(selectedPerson);
      
      if (!email) {
        toast({
          title: 'Email Not Found',
          description: `Could not find email for ${selectedPerson}`,
          variant: 'destructive'
        });
        return;
      }

      // Check if already exists
      if (recipients.some(r => r.email === email)) {
        toast({
          title: 'Already Added',
          description: `${selectedPerson} is already in the recipients list`,
          variant: 'destructive'
        });
        return;
      }

      // Add to Firestore
      const recipientDoc = doc(db, 'feedbackNotificationRecipients', email);
      await setDoc(recipientDoc, {
        email,
        name: selectedPerson,
        enabled: true
      });

      toast({
        title: 'Recipient Added',
        description: `${selectedPerson} will now receive feedback notifications`,
        variant: 'default'
      });

      // Reload recipients
      await loadRecipients();
      setSelectedPerson('');
    } catch (error) {
      console.error('Error adding recipient:', error);
      toast({
        title: 'Error',
        description: 'Failed to add recipient',
        variant: 'destructive'
      });
    }
  };

  const handleToggleEnabled = async (email: string, currentStatus: boolean) => {
    try {
      const recipientDoc = doc(db, 'feedbackNotificationRecipients', email);
      await updateDoc(recipientDoc, {
        enabled: !currentStatus
      });

      toast({
        title: currentStatus ? 'Recipient Disabled' : 'Recipient Enabled',
        description: `Feedback notifications ${currentStatus ? 'disabled' : 'enabled'} for this recipient`,
        variant: 'default'
      });

      await loadRecipients();
    } catch (error) {
      console.error('Error toggling recipient:', error);
      toast({
        title: 'Error',
        description: 'Failed to update recipient status',
        variant: 'destructive'
      });
    }
  };

  const handleRemoveRecipient = async (email: string, name?: string) => {
    try {
      const recipientDoc = doc(db, 'feedbackNotificationRecipients', email);
      await deleteDoc(recipientDoc);

      toast({
        title: 'Recipient Removed',
        description: `${name || email} removed from feedback notifications`,
        variant: 'default'
      });

      await loadRecipients();
    } catch (error) {
      console.error('Error removing recipient:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove recipient',
        variant: 'destructive'
      });
    }
  };

  const handleSendTestNotification = async () => {
    try {
      setIsSendingTest(true);

      // Create a test feedback entry
      const testFeedback = {
        userEmail: 'admin@jlsapps.com',
        userName: 'Admin Panel Test',
        appName: 'JLS Apps (Test)',
        message: '🧪 This is a test notification to verify that feedback notifications are working correctly. You can safely ignore this message.',
        timestamp: new Date(),
        isTest: true
      };

      // Add to feedback collection - this will trigger the notification function
      // Add to feedback collection
      const docRef = await addDoc(collection(db, 'feedback'), testFeedback);
      
      // Trigger email notification manually since there's no Firestore trigger
      const sendEmailFunction = httpsCallable(functions, 'sendFeedbackEmailNotification');
      await sendEmailFunction({ feedbackId: docRef.id });

      toast({
        title: 'Test Notification Sent',
        description: 'A test email has been sent to all enabled recipients. Check your inboxes!',
        variant: 'default'
      });
    } catch (error) {
      console.error('Error sending test notification:', error);
      toast({
        title: 'Test Failed',
        description: 'Failed to send test notification. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSendingTest(false);
    }
  };

  return (
    <Card className="p-6 dark:bg-gray-800">
      <div className="flex items-center gap-2 mb-4 cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
        <Mail className="h-6 w-6 text-purple-600 dark:text-purple-400" />
        <h2 className="text-xl font-semibold dark:text-white">Feedback Notification Recipients</h2>
        {isExpanded ? (
          <ChevronDown className="h-5 w-5 ml-auto dark:text-gray-400" />
        ) : (
          <ChevronLeft className="h-5 w-5 ml-auto dark:text-gray-400" />
        )}
      </div>

      {isExpanded && (
        <div className="space-y-6">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Manage who receives email notifications when users submit feedback through the app.
          </p>

          {/* Add Recipient Section */}
          <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-md">
            <h3 className="text-sm font-medium mb-3 dark:text-white">Add New Recipient</h3>
            <div className="flex gap-2">
              <div className="flex-1">
                <PersonSelect
                  value={selectedPerson}
                  onValueChange={setSelectedPerson}
                  people={availablePeople}
                  label="Select person"
                  portalContainer={null}
                />
              </div>
              <Button
                onClick={handleAddRecipient}
                disabled={!selectedPerson}
                className="bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Add
              </Button>
            </div>
          </div>

          {/* Test Notification Button */}
          {recipients.filter(r => r.enabled).length > 0 && (
            <div className="bg-green-50 dark:bg-green-900/30 p-4 rounded-md">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium dark:text-white mb-1">Test Notifications</h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Send a test email to all enabled recipients ({recipients.filter(r => r.enabled).length} users)
                  </p>
                </div>
                <Button
                  onClick={handleSendTestNotification}
                  disabled={isSendingTest}
                  className="bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600"
                >
                  <Send className={`h-4 w-4 mr-2 ${isSendingTest ? 'animate-pulse' : ''}`} />
                  {isSendingTest ? 'Sending...' : 'Send Test Email'}
                </Button>
              </div>
            </div>
          )}

          {/* Recipients List */}
          <div>
            <h3 className="text-sm font-medium mb-3 dark:text-white">
              Current Recipients ({recipients.filter(r => r.enabled).length} active)
            </h3>
            {isLoading ? (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                Loading recipients...
              </div>
            ) : recipients.length === 0 ? (
              <div className="text-center py-6 bg-gray-50 dark:bg-gray-700/50 rounded-md">
                <p className="text-gray-500 dark:text-gray-400">
                  No feedback notification recipients configured yet.
                </p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                  Add someone above to start receiving notifications.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {recipients.map((recipient) => (
                  <div
                    key={recipient.email}
                    className={`flex items-center justify-between p-3 rounded-md border ${
                      recipient.enabled
                        ? 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600'
                        : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 opacity-60'
                    }`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium dark:text-white">
                          {recipient.name || recipient.email}
                        </span>
                        {!recipient.enabled && (
                          <span className="text-xs px-2 py-0.5 bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded">
                            Disabled
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {recipient.email}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleToggleEnabled(recipient.email, recipient.enabled)}
                        className="dark:border-gray-600 dark:text-white dark:hover:bg-gray-600"
                      >
                        {recipient.enabled ? 'Disable' : 'Enable'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRemoveRecipient(recipient.email, recipient.name)}
                        className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 dark:border-gray-600"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
};

export default FeedbackNotificationRecipientsSection;